import { useEffect } from 'react';
import { expireSession, verifySession } from '../config/session';
import { AUTH_EVENT, clearAuth, getSessionExpiry, isSignedIn } from './useAuthStorage';

/** How often an open tab re-confirms the session with the server. */
const VERIFY_INTERVAL_MS = 5 * 60 * 1000;

/** Focus and tab switches can fire in bursts; one check covers them all. */
const MIN_VERIFY_GAP_MS = 60 * 1000;

/** setTimeout overflows above ~24.8 days and would fire immediately. */
const MAX_TIMER_MS = 2 ** 31 - 1;

// Module level so StrictMode's double-mounted effect shares one throttle.
let lastVerifiedAt = 0;

/**
 * Keeps the header's "signed in" state honest. Before this, the client
 * trusted whatever token sat in localStorage until some protected
 * request happened to fail, so a session the server had already ended
 * could keep showing as logged in indefinitely.
 *
 *  - The token's own expiry is scheduled locally, so the UI logs out the
 *    moment the server stops accepting it.
 *  - The server is asked directly on load, on focus and periodically,
 *    which catches logouts the client cannot predict (revoked tokens,
 *    a deleted account, a rotated secret).
 */
const useSessionWatch = () => {
  useEffect(() => {
    let timer;
    let initial = true;

    const schedule = () => {
      clearTimeout(timer);
      const expiresAt = isSignedIn() ? getSessionExpiry() : null;
      const isInitial = initial;
      initial = false;
      if (!expiresAt) return;

      const remaining = expiresAt - Date.now();
      if (remaining > 0) {
        timer = setTimeout(schedule, Math.min(remaining, MAX_TIMER_MS));
      } else if (isInitial) {
        // Already expired before this visit started: sign out quietly
        // rather than greeting a returning shopper with an alert.
        clearAuth();
      } else {
        expireSession();
      }
    };

    schedule();
    window.addEventListener(AUTH_EVENT, schedule);
    window.addEventListener('storage', schedule);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(AUTH_EVENT, schedule);
      window.removeEventListener('storage', schedule);
    };
  }, []);

  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== 'visible' || !isSignedIn()) return;
      if (Date.now() - lastVerifiedAt < MIN_VERIFY_GAP_MS) return;
      lastVerifiedAt = Date.now();
      verifySession();
    };

    check();
    const interval = setInterval(check, VERIFY_INTERVAL_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
};

export default useSessionWatch;
